<script setup lang="ts">
import {
  inject, onMounted, reactive, type Ref, ref,
} from 'vue';
import {
  CdrContainer, CdrLink, CdrText,
} from '@rei/cedar';
import { CompassSpinner } from '@rei/spinner';

import { getOrderByYear, sendClickAnalytics } from '../../utils';
import tags from '../../utils/globalTags';
import { getStorageValues } from '../../common/utils';
import { type IOrderDetails, IOrderDetailsKeys } from '../../pages/helpCenter/models/interfaces/OrderDetails';
import OrdersListComponent from './OrdersListComponent.vue';
import useAdobeExperiment from '../../common/composables/useAdobeExperiment';

const store: any = inject('store');
const props: IOrderDetails = store.getValues(IOrderDetailsKeys);

const { adobeExperimentValue: adobeExperimentMembershipInfoValue } = useAdobeExperiment({
  adobeTargetLocation: 'help-membership-info-help-homepage',
});
const { checkOrderLength, userInfo } = defineProps<{ checkOrderLength?: any, userInfo: Record<string, string|boolean>, }>();

const ordersByYear: Ref<any> = ref([]);
const state = reactive({
  hasError: false,
  status: 'loading',
  orderId: '',
});

onMounted(async () => {
  const { orderId } = state;
  try {
    if (orderId) {
      const sessionFilteredOrder = getStorageValues('filteredOrder');
      ordersByYear.value = sessionFilteredOrder ? JSON.parse(sessionFilteredOrder)
        : await getOrderByYear(props.showPreviousOrderDays, props.statusForOrdersNotShown, userInfo, props.maxNoOfOrders);
    } else {
      ordersByYear.value = await getOrderByYear(props.showPreviousOrderDays, props.statusForOrdersNotShown, userInfo, props.maxNoOfOrders);
      checkOrderLength(ordersByYear.value);
    }
  } catch (err) {
    state.hasError = true;
  } finally {
    state.status = 'done';
  }
});

const metricHandler = (source = 'online') => {
  const name = props.pageName ? props.pageName : tags.HELP;
  const clickData = {
    pageName: name,
    linkName: `${tags.HELP_CENTER_MY_ORDERS}_${source}`,
  };
  sendClickAnalytics(clickData);
};

</script>

<template>
  <cdr-container
    id="order-details"
    tag="section"
    class="order-details"
  >
    <div
      v-if="state.status === 'loading'"
      class="order-details__spinner-wrapper"
      data-ui="order-details--spinner"
    >
      <CompassSpinner class="order-details__spinner" />
    </div>
    <div
      v-else
      class="order-details__card-links"
    >
      <cdr-link
        v-if="props.orderId"
        class="order-details-back-help-home"
        href="/help"
        @click="metricHandler('back_help_home')"
      >
        Back to REI Help Center
      </cdr-link>
      <cdr-text
        tag="h2"
        tabindex="0"
        class="order-details__heading-links order-details__heading-links--welcome"
        v-show="adobeExperimentMembershipInfoValue !== 'B' || !userInfo?.isMember"
      >
        Hi, {{ userInfo?.firstName || 'User' }}
      </cdr-text>
      <cdr-text
        tag="h2"
        tabindex="0"
        class="order-details__heading-links"
      >
        Get help with your {{ ordersByYear?.length > 1 ? 'recent orders' : 'order' }}:
      </cdr-text>
      <!-- List of Orders -->
      <div v-if="ordersByYear?.length > 0">
        <OrdersListComponent
          :ordersByYear="ordersByYear"
          :orderId="state.orderId"
        />
      </div>
      <cdr-link
        class="order-details__card-links-text"
        href="/PurchaseHistoryView"
        @click="metricHandler('Purchase_History')"
      >
        View order history for more
      </cdr-link>
    </div>
  </cdr-container>
</template>

<style lang="scss">
</style>
